import {
  getContrastColor,
  hexToRgba,
  addAlphaToRgb,
  applyOpacityToGradient,
  getSystemMessageTextColor,
  getContrastTextColor,
} from './color.utils';

export interface ThemeSettings {
  __typename?:
    | 'SurveyProjectFrontendThemeSettings'
    | 'ProjectFrontendThemeSettings';
  bgColor?: string | null;
  bgImage?: string | null;
  font?: string | null;
  id: string;
  mode?: 'LIGHT' | 'DARK' | 'AUTO' | null;
  primaryColor?: string | null;
  projectId: string;
  secondaryColor?: string | null;
  transparency?: number | null;
}

export interface ThemeDefaults {
  primaryColor: string;
  secondaryColor: string;
  font: string;
  bgColor: string;
  mode: 'LIGHT' | 'DARK';
  transparency: number;
  managerConfigBgImage?: string;
  bgImage?: string;
  basePath?: string;
}

export const DEFAULT_THEME_SETTINGS: ThemeDefaults = {
  primaryColor: '#272753',
  secondaryColor: '#000000',
  font: 'Inter',
  bgColor: '#F1F1F8',
  mode: 'LIGHT',
  transparency: 100,
};

const DARK_MODE_TEXT_COLOR = '#ffffff';
const LIGHT_MODE_TEXT_COLOR = '#000000';
const DARK_MODE_UNSELECTED_BG_COLOR = '#FFFFFF3D';
const LIGHT_MODE_UNSELECTED_BG_COLOR = '#E3E3F2';

export function toThemeMode(mode: string | null | undefined): 'LIGHT' | 'DARK' {
  if (!mode) return 'LIGHT';
  const upper = mode.toUpperCase();
  if (upper === 'DARK') return 'DARK';
  return 'LIGHT';
}

export function getFontCssVariable(fontName: string): string {
  if (fontName === 'Arial') {
    return 'Arial, sans-serif';
  }

  const cssVarName = `--font-${fontName.toLowerCase().replace(/\s+/g, '-')}`;
  return `var(${cssVarName}, ${fontName}), sans-serif`;
}

export interface OrgThemeSettings {
  brandColor?: string | null;
  font?: string | null;
  backgroundColor?: string | null;
  themeMode?: string | null;
  transparency?: number | null;
  selectedBackground?: string | null;
}

export type SurveyThemeOrgSettings = OrgThemeSettings & {
  logoUrl?: string | null;
  avatarUrl?: string | null;
};

export interface SurveyThemeInput {
  themeSettings: ThemeSettings | null | undefined;
  defaults?: Partial<ThemeDefaults>;
  accentColor?: string | null;
  orgSettings?: SurveyThemeOrgSettings | null;
  forceSystemDefaults?: boolean;
}

export function mapOrgSettingsToThemeSettings(
  org: OrgThemeSettings | null | undefined
): ThemeSettings | null {
  if (!org) return null;
  const hasBranding =
    org.brandColor ?? org.font ?? org.backgroundColor ?? org.selectedBackground;
  if (!hasBranding) return null;

  const mode: 'LIGHT' | 'DARK' =
    String(org.themeMode).toUpperCase() === 'DARK' ? 'DARK' : 'LIGHT';
  const secondaryColor = getContrastTextColor(
    org.backgroundColor ?? null,
    org.selectedBackground ?? null,
    mode === 'DARK' ? 'dark' : 'light'
  );

  return {
    id: 'org',
    projectId: '',
    primaryColor: org.brandColor ?? undefined,
    font: org.font ?? undefined,
    bgColor: org.backgroundColor ?? undefined,
    mode,
    transparency: org.transparency ?? undefined,
    bgImage: org.selectedBackground ?? undefined,
    secondaryColor,
  };
}

export function getBrandedGradient(
  accentColor: string | null | undefined
): string | null {
  if (!accentColor || accentColor.includes('gradient')) return null;
  return `linear-gradient(225deg, #ffffff 40%, ${accentColor} 100%)`;
}

export function createThemeSettingsStyle(
  themeSettings: ThemeSettings | null | undefined,
  overrides?: Partial<ThemeDefaults>,
  brandingAccentColor?: string | null,
  orgSettings?: OrgThemeSettings | null,
  forceSystemDefaults?: boolean
): Record<string, string> {
  const defaults = { ...DEFAULT_THEME_SETTINGS, ...overrides };

  const resolveImagePath = (
    imagePath: string | null | undefined
  ): string | null | undefined => {
    if (!imagePath) return imagePath;

    const basePath =
      defaults.basePath ??
      (typeof process !== 'undefined'
        ? process.env['NEXT_PUBLIC_ASSET_BASE_PATH']
        : undefined);

    if (!basePath) return imagePath;

    if (
      imagePath.startsWith('/') &&
      !imagePath.startsWith('//') &&
      !imagePath.startsWith(basePath)
    ) {
      return `${basePath}${imagePath}`;
    }
    return imagePath;
  };

  const effective = (() => {
    if (forceSystemDefaults) return null;
    if (themeSettings != null) return themeSettings;
    return mapOrgSettingsToThemeSettings(orgSettings ?? undefined);
  })();
  const hasThemeSettings = effective !== null && effective !== undefined;

  const primaryColor = (() => {
    if (forceSystemDefaults) return defaults.primaryColor;
    if (effective?.primaryColor) return effective.primaryColor;
    if (brandingAccentColor) return brandingAccentColor;
    return defaults.primaryColor;
  })();
  const backgroundColor = (() => {
    if (forceSystemDefaults) return defaults.bgColor;
    if (effective?.bgColor) return effective.bgColor;
    const gradient = getBrandedGradient(primaryColor);
    if (gradient) return gradient;
    return defaults.bgColor;
  })();
  const font = effective?.font || defaults.font;
  const mode = toThemeMode(effective?.mode) || defaults.mode;
  const transparency = effective?.transparency ?? defaults.transparency;
  const secondaryColor = effective?.secondaryColor || defaults.secondaryColor;

  const bgImage =
    effective?.bgImage === defaults.managerConfigBgImage
      ? defaults.bgImage
      : effective?.bgImage || defaults.bgImage;

  const isGradient = backgroundColor.includes('gradient');
  const isDarkMode = mode === 'DARK';
  const isLightMode = mode === 'LIGHT';
  const modeOverlayColor = isLightMode ? '#ffffff' : '#000000';
  const modeOverlayOpacity = isLightMode ? 0.1 : 0.3;
  const modeOverlay = hexToRgba(modeOverlayColor, modeOverlayOpacity);

  let finalBgImage: string;

  if (bgImage) {
    if (isGradient) {
      if (hasThemeSettings) {
        const gradientWithOpacity = applyOpacityToGradient(
          backgroundColor,
          0.64
        );
        finalBgImage = `linear-gradient(${modeOverlay}, ${modeOverlay}), ${gradientWithOpacity}, url(${resolveImagePath(bgImage)})`;
      } else {
        finalBgImage = `url(${resolveImagePath(bgImage)})`;
      }
    } else {
      if (hasThemeSettings) {
        const bgColorOverlay = (() => {
          if (backgroundColor.startsWith('#'))
            return hexToRgba(backgroundColor, 0.64);
          if (backgroundColor.startsWith('rgb'))
            return addAlphaToRgb(backgroundColor, 0.64);
          return backgroundColor;
        })();
        finalBgImage = `linear-gradient(${modeOverlay}, ${modeOverlay}), linear-gradient(${bgColorOverlay}, ${bgColorOverlay}), url(${resolveImagePath(bgImage)})`;
      } else {
        finalBgImage = `url(${resolveImagePath(bgImage)})`;
      }
    }
  } else if (isGradient) {
    finalBgImage = `linear-gradient(${modeOverlay}, ${modeOverlay}), ${backgroundColor}`;
  } else {
    finalBgImage = backgroundColor
      ? `linear-gradient(${modeOverlay}, ${modeOverlay}), linear-gradient(${backgroundColor}, ${backgroundColor})`
      : '';
  }

  const unselectedBgColor = (() => {
    if (!hasThemeSettings || mode === null || mode === undefined)
      return LIGHT_MODE_UNSELECTED_BG_COLOR;
    if (isDarkMode) return DARK_MODE_UNSELECTED_BG_COLOR;
    return LIGHT_MODE_UNSELECTED_BG_COLOR;
  })();

  const unselectedTextColor =
    isDarkMode && hasThemeSettings
      ? DARK_MODE_TEXT_COLOR
      : LIGHT_MODE_TEXT_COLOR;

  const primaryContrastColor = getContrastColor(primaryColor);

  const textOnBgImageColor = getContrastTextColor(
    backgroundColor,
    resolveImagePath(bgImage) ?? null,
    isDarkMode ? 'dark' : 'light'
  );

  const textOnAssistantMessageColor = getSystemMessageTextColor(
    backgroundColor,
    resolveImagePath(bgImage) ?? null,
    isDarkMode ? 'dark' : 'light',
    transparency
  );

  const cssVariables: Record<string, string> = {
    '--brand-primary': primaryColor,
    '--brand-secondary': secondaryColor,
    '--brand-font': getFontCssVariable(font),
    '--brand-transparency': `${transparency}`,
    '--brand-bg-color': (() => {
      if (bgImage) return 'transparent';
      if (isGradient) return 'transparent';
      return backgroundColor;
    })(),
    '--brand-bg-color-only': backgroundColor,
    '--brand-text-unselected': unselectedTextColor,
    '--brand-primary-contrast': primaryContrastColor,
    '--brand-bg-unselected': unselectedBgColor,
    '--brand-bg-image': finalBgImage || '',
    '--brand-text-on-bg-image': textOnBgImageColor,
    '--brand-text-on-assistant-message': textOnAssistantMessageColor,
    '--brand-separation-shadow':
      transparency === 100 ? '0 2px 0 0 #E3E3F2' : 'none',
    '--brand-separation-border':
      transparency === 100 ? '#E3E3F2' : 'transparent',
    '--brand-assistant-message-bg':
      'rgba(255, 255, 255, calc(var(--brand-transparency) / 100))',
    '--brand-assistant-message-bg-secondary':
      'rgba(227, 227, 242, calc(var(--brand-transparency) / 100))',
    '--brand-open-end-input-bg': hasThemeSettings ? '#F3F4F6' : '#E3E3F2',
  };

  return cssVariables;
}
